/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid/v4';
import { requestBot } from './services/Chatbot';

import ChatBotContainer from './components/ChatBotContainer';
import Header from './components/Header';
import ContentChatBot from './components/ContentChatBot';
import ContentBot from './components/ContentBot';
import ContentUser from './components/ContentUser';
import ContentButtons from './components/ContentButtons';
import ContentListQuestions from './components/ContentListQuestions';
import ContentVideo from './components/ContentVideo';
import InputForm from './components/InputForm';
import CarouselStep from './components/CarouselStep';
import Loading from './components/Loading';
import Footer from './components/Footer';

const SESSION_ID = uuidv4();

const REQUEST_BOT = {
  id: 1,
  event: 'event',
  message: 'Welcome',
};

const INPUT_SETTINGS = {
  required: false,
  disabled: false,
  inputMessage: '',
};

const App = ({
  botName,
  botTitle,
  headerImage,
  botImage,
  token,
  color,
  params,
}) => {
  const [{ required, disabled, inputMessage }, setInput] =
    useState(INPUT_SETTINGS);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState(false);
  const [messages, setMessage] = useState([]);

  /**
   * Function Request ChatBot
   * @param {object} body
   * @returns
   */
  async function requestChatBot(body) {
    if (body.message === '') {
      setInput({ required: true, disabled: false, inputMessage: '' });
      return;
    }

    setLoader(true);
    setInput({ required: false, disabled: true, inputMessage: '' });

    setMessage((prevState) => [
      ...prevState,
      {
        id: 1,
        event: body.event,
        message: inputMessage,
        parameters: body.parameters,
      },
    ]);

    const sendRequestBot = await requestBot(
      token,
      SESSION_ID,
      body.event,
      body.message,
      body.parameters
    );

    if (!sendRequestBot.status) {
      setErrors(true);
      return;
    }

    let time = 0;

    if (sendRequestBot.messages.length === 0) {
      setLoader(false);
      setInput({ required: false, disabled: false, inputMessage: '' });

      setMessage((prevState) => [
        ...prevState,
        {
          id: 2,
          event: body.event,
          message:
            'Desculpa, aconteceu um erro inesperado. Mas você pode digitar o que precisa!',
        },
      ]);
      return;
    }

    sendRequestBot.messages.forEach((data) => {
      time += 1;
      const count = time;

      setTimeout(async () => {
        if (count === time) {
          setLoader(false);
          setInput({ required: false, disabled: false, inputMessage: '' });
        }
        setMessage((prevState) => [...prevState, data]);
      }, time * 1500);
    });
  }

  /**
   * Function Get Messages
   * @returns
   */
  const getMessages = () => {
    const messageResponse = [];

    messages.forEach((element) => {
      if (element.id === 1 && element.message.toString().trim() !== '') {
        messageResponse.push(
          <ContentUser color={color} text={element.message} />
        );
      }

      if (element.id === 2 && element.message.toString().trim() !== '') {
        messageResponse.push(
          <ContentBot botImage={botImage} text={element.message} />
        );
      }

      if (element.id === 3) {
        messageResponse.push(
          <ContentButtons
            data={element}
            color={color}
            type="button"
            onClick={(message) => {
              setMessage((prevState) => [
                ...prevState,
                {
                  id: 1,
                  event: 'message',
                  message,
                },
              ]);
              requestChatBot({
                id: element.id,
                event: 'message',
                message,
              });
            }}
          />
        );
      }

      if (element.id === 4) {
        messageResponse.push(
          <ContentVideo
            url={element.suggestedActions[0].value}
            onPauseVideo={() =>
              requestChatBot({
                id: element.id,
                event: 'event',
                message: element.suggestedActions[0].title,
              })
            }
            onEndVideo={() =>
              requestChatBot({
                id: element.id,
                event: 'event',
                message: element.suggestedActions[0].title,
              })
            }
          />
        );
      }

      if (element.id === 5) {
        messageResponse.push(<ContentButtons type="link" data={element} />);
      }

      if (element.id === 6) {
        messageResponse.push(
          <ContentListQuestions
            botImage={botImage}
            data={element}
            onClick={(message) =>
              requestChatBot({
                id: element.id,
                event: 'message',
                message,
              })
            }
          />
        );
      }

      if (element.id === 7 || element.id === 8) {
        messageResponse.push(
          <CarouselStep
            rows={element}
            color={color}
            onClick={(message) =>
              requestChatBot({
                id: element.id,
                event: 'event',
                message,
              })
            }
          />
        );
      }
    });
    return messageResponse;
  };

  useEffect(() => {
    if (params.context) {
      REQUEST_BOT.message = params.context;
    }

    if (params.msisdn) {
      REQUEST_BOT.parameters = {
        msisdn: `+${params.msisdn}`,
      };
    }

    requestChatBot(REQUEST_BOT); // eslint-disable-next-line
  }, []);

  if (errors) {
    return <h1>FS Chatbot</h1>;
  }

  return (
    <ChatBotContainer>
      <Header botName={botName} botTitle={botTitle} headerImage={headerImage} />
      <ContentChatBot>
        {getMessages()}
        {loader && <Loading botImage={botImage} />}
      </ContentChatBot>
      <Footer>
        <InputForm
          inputMessage={inputMessage}
          required={required}
          disabled={disabled}
          onClick={() =>
            requestChatBot({
              id: 1,
              event: 'message',
              message: inputMessage,
            })
          }
          onChange={(event) =>
            setInput({
              inputMessage: event.target.value,
              required: false,
              disabled: false,
            })
          }
          onKeyPress={(event) =>
            event.key === 'Enter' &&
            requestChatBot({
              id: 1,
              event: 'message',
              message: inputMessage,
            })
          }
        />
      </Footer>
    </ChatBotContainer>
  );
};

App.defaultProps = {
  params: '',
};

App.propTypes = {
  botName: PropTypes.string.isRequired,
  botTitle: PropTypes.string.isRequired,
  headerImage: PropTypes.string.isRequired,
  botImage: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  color: PropTypes.object.isRequired,
  params: PropTypes.object,
};

export default App;
